import React, { useEffect, useState } from "react";
import { isHtmlDocUrl, resolveInventoryUrl, inventoryHref, photoUrl } from "../../api/InventoryAPI";
import {
    fetchAdmin1ByCountry,
    fetchAdmin2ByAdmin1,
    fetchCitiesByAdmin2,
    fetchCountries,
} from "../../api/GeoDataAPI";
import { ContactKind, postContactReveal } from "../../api/AnalyticsAPI";
import { getOrgClaimStatus, requestClaimLink, sendClaimLink } from "../../api/ProspectAPI";
import { CategoryParameter, ParameterDataType } from "../../types/Category";
import { ItemLocation, PhotoRef } from "../../types/Inventory";
import { Unit } from "../../types/Unit";
import { CoverageArea, areaLabel } from "../../utils/coverage";

interface Props {
    /** A search-result row (id, name, description, url, parameters, …). */
    item: any;
    /** Category parameters, to label/format the item's parameter values. */
    params: CategoryParameter[];
    units: Unit[];
    /** For the geodata name lookups of the store-location fallback. */
    token?: string;
    onClose: () => void;
}

/** "City, County, State, Country" resolved from geo ids (deepest first).
 *  Store profiles carry ids only, so names come from the geodata lists. */
async function resolveGeoNames(token: string, loc: ItemLocation): Promise<string> {
    const parts: string[] = [];
    const nameOf = (list: { id: string | number; name: string }[], id: number | null) =>
        id ? list.find((x) => Number(x.id) === id)?.name ?? null : null;

    const country = nameOf(await fetchCountries(token), loc.countryId);
    const admin1 = loc.countryId && loc.admin1Id
        ? nameOf(await fetchAdmin1ByCountry(token, String(loc.countryId)), loc.admin1Id) : null;
    const admin2 = loc.admin1Id && loc.admin2Id
        ? nameOf(await fetchAdmin2ByAdmin1(token, String(loc.admin1Id)), loc.admin2Id) : null;
    const city = loc.admin2Id && loc.cityId
        ? nameOf(await fetchCitiesByAdmin2(token, String(loc.admin2Id)), loc.cityId) : null;

    for (const p of [city, admin2, admin1, country]) if (p) parts.push(p);
    return parts.join(", ");
}

/** Divider above each content section (everything below the title row). */
const sectionStyle: React.CSSProperties = {
    borderTop: "2px solid var(--text-secondary, #6b7280)",
    marginTop: "0.75rem",
    paddingTop: "0.75rem",
};

/** Read-only details view opened when a searcher clicks a result row. */
const InventoryDetailModal: React.FC<Props> = ({ item, params, units, token, onClose }) => {
    const url: string | undefined = item?.url;
    const hasPage = !!url && isHtmlDocUrl(url); // legacy attached HTML page
    const isExternal = !!url && !hasPage;
    const photos: PhotoRef[] = Array.isArray(item?.photos)
        ? [...item.photos].sort((a: PhotoRef, b: PhotoRef) => a.order - b.order)
        : [];
    const coverage: CoverageArea[] = Array.isArray(item?.coverage) ? item.coverage : [];

    // Where: exactly one of service area / item location / store location
    // (the store's profile address is the fallback when the listing carries
    // neither). Store locations hold ids only, so names resolve async.
    const itemLocation: ItemLocation | null = item?.location ?? null;
    const storeLocation: ItemLocation | null =
        coverage.length === 0 && !itemLocation ? item?.storeLocation ?? null : null;
    const [storePlace, setStorePlace] = useState<string | null>(null);
    useEffect(() => {
        setStorePlace(null);
        if (!storeLocation) return;
        if (storeLocation.label) { setStorePlace(storeLocation.label); return; }
        let cancelled = false;
        resolveGeoNames(token ?? "", storeLocation)
            .then((s) => { if (!cancelled && s) setStorePlace(s); })
            .catch(() => {}); // fallback text only — never break the modal
        return () => { cancelled = true; };
        // Deliberately keyed on the item id only: the location fields cannot
        // change while the same result row's modal is open.
    }, [item?.id]);

    const streetOf = (loc: ItemLocation) =>
        [loc.streetAddress1, loc.streetAddress2].filter(Boolean).join(", ");

    // Contact: phone/email start hidden behind a reveal click (blunts
    // scrapers, and each reveal is the lead metric — beacon fired once per
    // channel per open). Website/WhatsApp are plain links; clicks beacon too.
    const contact = item?.storeContact ?? null;

    // Unclaimed managed org? Then the listing invites its owner to claim it.
    // The claim link itself only travels by email — the page never sees it.
    // mode "send": one click mails it to the trusted address on file;
    // mode "request": the visitor leaves their own email, the admin verifies.
    const [claimStatus, setClaimStatus] = useState<{ unclaimed: boolean; mode: "send" | "request" | null; maskedEmail: string | null } | null>(null);
    const [claimLinkSent, setClaimLinkSent] = useState(false);
    const [claimBusy, setClaimBusy] = useState(false);
    const [claimName, setClaimName] = useState("");
    const [claimEmail, setClaimEmail] = useState("");
    useEffect(() => {
        setClaimStatus(null);
        setClaimLinkSent(false);
        setClaimName("");
        setClaimEmail("");
        if (item?.orgId == null) return;
        let cancelled = false;
        getOrgClaimStatus(item.orgId)
            .then((s) => { if (!cancelled && s.unclaimed) setClaimStatus(s); })
            .catch(() => {}); // banner only — never break the modal
        return () => { cancelled = true; };
    }, [item?.id, item?.orgId]);
    const onSendClaimLink = async () => {
        if (item?.orgId == null) return;
        setClaimBusy(true);
        try { await sendClaimLink(item.orgId); } catch { /* still confirm — see below */ }
        finally { setClaimLinkSent(true); setClaimBusy(false); }
    };
    const onRequestClaimLink = async () => {
        if (item?.orgId == null || !claimEmail.trim()) return;
        setClaimBusy(true);
        try { await requestClaimLink(item.orgId, claimName, claimEmail); } catch { /* confirm anyway */ }
        finally { setClaimLinkSent(true); setClaimBusy(false); }
    };

    const [revealed, setRevealed] = useState<Set<ContactKind>>(new Set());
    const beacon = (kind: ContactKind) => {
        postContactReveal(item.id, item.categoryId, kind, token).catch(() => {});
    };
    const reveal = (kind: ContactKind) => {
        if (revealed.has(kind)) return;
        setRevealed(new Set(revealed).add(kind));
        beacon(kind);
    };

    const formatParam = (param: CategoryParameter) => {
        const found = item.parameters?.find((p: any) => p.name === param.name);
        const raw = found?.value ?? "";
        const value = Array.isArray(raw)
            ? raw.map((v: any) => (typeof v === "object" && v !== null ? v.name : v)).join(", ")
            : typeof raw === "object" && raw !== null
            ? raw.name ?? ""
            : raw;
        const isNumeric =
            param.dataType === ParameterDataType.INTEGER || param.dataType === ParameterDataType.DOUBLE;
        const unitId = isNumeric ? (found?.units ?? param.unitId) : undefined;
        const unitLabel = unitId ? units.find((u) => u.id === unitId)?.name ?? unitId : null;
        let display = String(value ?? "");
        if (param.dataType === ParameterDataType.BOOLEAN && display) {
            display = display.toLowerCase() === "true" ? "Yes" : "No";
        }
        return { value: display, unitLabel };
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                padding: "3vh 1rem", zIndex: 1000, overflowY: "auto",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "var(--surface, #fff)", color: "var(--text-primary, #111)",
                    borderRadius: "8px", width: "min(820px, 100%)", maxWidth: "100%",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.25)", padding: "1rem 1.25rem",
                }}
            >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                    <h2 style={{ margin: 0, flex: 1, fontSize: "1.25rem" }}>{item.name}</h2>
                    <button type="button" className="btn-sm" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* Unclaimed managed org: honesty label + the owner's way in.
                    (Yelp-style: the page was built from public information.) */}
                {claimStatus?.unclaimed && (
                    <div style={{
                        border: "1px solid #16a34a", borderRadius: "6px", background: "#f0fdf4",
                        padding: "0.5rem 0.75rem", margin: "0.75rem 0", fontSize: "0.9rem",
                    }}>
                        {claimLinkSent ? (
                            <strong>
                                {claimStatus.mode === "send"
                                    ? `Claim link sent to ${claimStatus.maskedEmail} — check that inbox.`
                                    : "Thanks — we'll verify and email your ownership link shortly."}
                            </strong>
                        ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                                <span>
                                    <strong>Unclaimed business page</strong>{" "}
                                    <span style={{ color: "var(--text-secondary)" }}>
                                        — listed by BabaHanks from public information. Is this
                                        your business?
                                    </span>
                                </span>
                                {claimStatus.mode === "send" ? (
                                    <button type="button" className="btn-sm" onClick={onSendClaimLink} disabled={claimBusy}
                                            title={`We'll email the take-ownership link to ${claimStatus.maskedEmail ?? "the address on file"}`}>
                                        {claimBusy ? "Sending…" : `Email me the claim link (${claimStatus.maskedEmail ?? "address on file"})`}
                                    </button>
                                ) : (
                                    // No trusted owner address on file: take the
                                    // visitor's own email; the admin verifies before
                                    // any ownership link is sent.
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                                        <span style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                                            Tell us where to send the ownership link — we'll verify first:
                                        </span>
                                        <input value={claimName} onChange={(e) => setClaimName(e.target.value)}
                                               placeholder="your name" disabled={claimBusy}
                                               style={{ width: "9rem" }} />
                                        <input value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)}
                                               placeholder="you@yourbusiness.com" type="email" disabled={claimBusy}
                                               style={{ width: "13rem" }} />
                                        <button type="button" className="btn-sm" onClick={onRequestClaimLink}
                                                disabled={claimBusy || !claimEmail.trim()}>
                                            {claimBusy ? "Sending…" : "Request claim link"}
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Who + how to reach them. */}
                {(item.storeName || contact) && (
                    <div style={sectionStyle}>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.3rem" }}>
                            Contact{item.storeName ? ` — ${item.storeName}` : ""}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                            {contact?.phone && (revealed.has("PHONE") ? (
                                <a href={`tel:${contact.phone}`} onClick={() => beacon("PHONE")}>📞 {contact.phone}</a>
                            ) : (
                                <button type="button" className="btn-sm" onClick={() => reveal("PHONE")}>
                                    <span style={{ fontSize: "1.1em" }}>📞</span> Show phone number
                                </button>
                            ))}
                            {contact?.email && (revealed.has("EMAIL") ? (
                                <a href={`mailto:${contact.email}`}>📧 {contact.email}</a>
                            ) : (
                                <button type="button" className="btn-sm" onClick={() => reveal("EMAIL")}>
                                    <span style={{ fontSize: "1.1em" }}>📧</span> Show email
                                </button>
                            ))}
                            {contact?.whatsapp && (
                                <a
                                    href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`}
                                    target="_blank" rel="noopener noreferrer"
                                    onClick={() => beacon("WHATSAPP")}
                                >
                                    💬 WhatsApp
                                </a>
                            )}
                            {contact?.website && (
                                <a
                                    href={contact.website.startsWith("http") ? contact.website : `https://${contact.website}`}
                                    target="_blank" rel="noopener noreferrer"
                                    onClick={() => beacon("WEBSITE")}
                                >
                                    🌐 Website
                                </a>
                            )}
                            {!contact && (
                                <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                                    No contact details provided.
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Where: service area, OR the item's own location, OR (as a
                    fallback) the store's profile address — right under the name. */}
                {coverage.length > 0 && (
                    <div style={sectionStyle}>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.3rem" }}>Service area</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                            {coverage.map((c) => (
                                <span key={`${c.level}:${c.id}`} style={{ background: "var(--surface-2, #f3f4f6)", borderRadius: "999px", padding: "0.15rem 0.6rem", fontSize: "0.85rem" }}>
                                    {areaLabel(c)}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {coverage.length === 0 && itemLocation && (
                    <div style={sectionStyle}>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.3rem" }}>Location</div>
                        <div>
                            📍 {[streetOf(itemLocation), itemLocation.label].filter(Boolean).join(", ")}
                        </div>
                    </div>
                )}
                {storeLocation && (
                    <div style={sectionStyle}>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem", marginBottom: "0.3rem" }}>Store location</div>
                        <div>
                            📍 {[streetOf(storeLocation), storePlace].filter(Boolean).join(", ") || "—"}
                        </div>
                    </div>
                )}

                {params.length > 0 && (
                    <table style={{ ...sectionStyle, width: "100%", borderCollapse: "collapse", marginBottom: "1rem" }}>
                        <tbody>
                            {params.map((param) => {
                                const { value, unitLabel } = formatParam(param);
                                if (!value) return null;
                                return (
                                    <tr key={param.name}>
                                        <td style={{ padding: "2px 8px 2px 0", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                                            {param.name}
                                        </td>
                                        <td style={{ padding: "2px 0" }}>
                                            {value}
                                            {unitLabel && <span style={{ marginLeft: "0.3rem", color: "var(--text-secondary)" }}>{unitLabel}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {item.description && (
                    <p style={{ ...sectionStyle, whiteSpace: "pre-wrap", marginBottom: "0.75rem", color: "var(--text-secondary)" }}>
                        {item.description}
                    </p>
                )}

                {/* Photo gallery (structured assets). */}
                {photos.length > 0 && (
                    <div style={{ ...sectionStyle, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "8px" }}>
                        {photos.map((p) => (
                            <a key={p.photoId} href={photoUrl(item.id, p.photoId)} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={photoUrl(item.id, p.photoId)}
                                    alt=""
                                    style={{ width: "100%", height: "auto", borderRadius: "6px", display: "block" }}
                                />
                            </a>
                        ))}
                    </div>
                )}

                {/* Attached HTML page, shown inline — ONLY for pages stored in
                    our system (isHtmlDocUrl); external links get just the
                    button below. The sandbox allows scripts so the page
                    renders, but withholds allow-same-origin, so the
                    seller-supplied HTML runs in an opaque origin with no
                    access to our cookies, storage, or APIs. */}
                {hasPage && (
                    <div style={sectionStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.3rem" }}>
                            <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Attached page</span>
                            <a href={resolveInventoryUrl(url!)} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem" }}>
                                open in a new tab ↗
                            </a>
                        </div>
                        <iframe
                            title={item.name}
                            src={resolveInventoryUrl(url!)}
                            sandbox="allow-scripts allow-popups"
                            style={{ width: "100%", height: "60vh", border: "1px solid var(--border, #ddd)", borderRadius: "6px" }}
                        />
                    </div>
                )}

                {isExternal && (
                    <div style={sectionStyle}>
                        <a href={inventoryHref(url!)} target="_blank" rel="noopener noreferrer" className="btn-sm">
                            View original page ↗
                        </a>
                    </div>
                )}

                {!url && photos.length === 0 && (
                    <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                        No photos or link attached to this listing.
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryDetailModal;
